import React, { type FC, type ReactNode, useCallback, useId } from 'react'
import type { SxProps, Theme } from '@mui/material'
import {
  Alert,
  Box,
  Button,
  CircularProgress,
  Dialog,
  DialogContent,
  TablePagination,
  Typography,
} from '@mui/material'
import { CloseButton } from '@/common/components'
import { visuallyHiddenStyle } from './tableStyles'

interface PaginatedListModalProps {
  readonly open: boolean
  readonly onClose: () => void
  readonly title: string
  readonly totalElements: number | null
  readonly page: number
  readonly pageSize: number
  readonly onPageChange: (newPage: number) => void
  readonly isLoading: boolean
  readonly isError: boolean
  readonly error: string | null
  readonly isEmpty: boolean
  readonly emptyMessage: string
  readonly onRetry?: () => void
  readonly toolbar?: ReactNode
  readonly bulkActionBar?: ReactNode
  readonly children: ReactNode
}

export const PaginatedListModal: FC<PaginatedListModalProps> = ({
  open,
  onClose,
  title,
  totalElements,
  page,
  pageSize,
  onPageChange,
  isLoading,
  isError,
  error,
  isEmpty,
  emptyMessage,
  onRetry,
  toolbar,
  bulkActionBar,
  children,
}) => {
  const titleId = useId()

  const handleDialogClose = useCallback(
    (_event: unknown, reason: string) => {
      if (reason === 'backdropClick') return
      onClose()
    },
    [onClose],
  )

  const handlePageChange = useCallback(
    (_event: React.MouseEvent<HTMLButtonElement> | null, newPage: number) => {
      onPageChange(newPage)
    },
    [onPageChange],
  )

  const fitsOnOnePage = totalElements !== null && totalElements <= pageSize

  return (
    <Dialog
      open={open}
      onClose={handleDialogClose}
      maxWidth="md"
      fullWidth
      aria-labelledby={titleId}
      sx={dialogSx}
    >
      <CloseButton onClick={onClose} sx={closeButtonSx} />

      <DialogContent sx={contentSx}>
        <Box sx={headerSx}>
          <Typography id={titleId} variant="h6" component="h2">
            {title}
          </Typography>
          <Typography
            variant="body2"
            sx={{
              color: 'text.disabled',
              ml: 1,
              visibility: totalElements === null ? 'hidden' : 'visible',
            }}
          >
            — {totalElements ?? 0} total
          </Typography>
        </Box>

        {toolbar}

        {bulkActionBar}

        {isLoading && (
          <Box sx={centeredSx}>
            <CircularProgress size={32} role="status" />
            <Typography component="span" style={visuallyHiddenStyle} aria-live="polite">
              Loading...
            </Typography>
          </Box>
        )}

        {isError && (
          <Alert
            severity="error"
            action={
              onRetry ? (
                <Button color="inherit" size="small" onClick={onRetry}>
                  Retry
                </Button>
              ) : undefined
            }
          >
            {error ?? 'An unexpected error occurred.'}
          </Alert>
        )}

        {!isLoading && !isError && isEmpty && (
          <Typography sx={{ color: 'text.disabled', textAlign: 'center', py: 4 }}>
            {emptyMessage}
          </Typography>
        )}

        {!isLoading && !isError && !isEmpty && children}

        <Box sx={{ minHeight: 52 }}>
          {!fitsOnOnePage && (
            <TablePagination
              component="div"
              count={totalElements ?? 0}
              page={page}
              rowsPerPage={pageSize}
              rowsPerPageOptions={[]}
              onPageChange={handlePageChange}
            />
          )}
        </Box>
      </DialogContent>
    </Dialog>
  )
}

// ---------------------------------------------------------------------------
// Styles
// ---------------------------------------------------------------------------

const dialogSx: SxProps<Theme> = {
  '& .MuiDialog-paper': {
    borderRadius: 2,
    position: 'relative',
  },
}

const closeButtonSx: SxProps<Theme> = {
  position: 'absolute',
  right: 12,
  top: 12,
}

const contentSx: SxProps<Theme> = {
  pt: 5,
  pb: 1,
  px: 3,
}

const headerSx: SxProps<Theme> = {
  display: 'flex',
  alignItems: 'baseline',
  mb: 2,
}

const centeredSx: SxProps<Theme> = {
  display: 'flex',
  justifyContent: 'center',
  alignItems: 'center',
  py: 6,
  position: 'relative',
}
